// src/pages/Admin/PagesPersonnalisees/components/PageForm.jsx

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FaLink, FaSave, FaTimes } from "react-icons/fa";

import {
  buildInitialFormFromPage,
  generateSlug,
  normalizePagePayload,
  validatePageForm,
} from "../utils/pagesPersonnaliseesUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-cyan-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

export default function PageForm({
  mode = "create",
  page,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(() => buildInitialFormFromPage(page), [page]);

  const [formData, setFormData] = useState(initialForm);
  const [slugTouched, setSlugTouched] = useState(mode === "edit");

  useEffect(() => {
    setFormData(initialForm);
    setSlugTouched(mode === "edit");
  }, [initialForm, mode]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => {
      const nextValue = type === "checkbox" ? checked : value;

      if (name === "titre" && mode === "create" && !slugTouched) {
        return {
          ...prev,
          titre: value,
          slug: generateSlug(value),
        };
      }

      return {
        ...prev,
        [name]: nextValue,
      };
    });
  };

  const handleSlugChange = (event) => {
    setSlugTouched(true);

    setFormData((prev) => ({
      ...prev,
      slug: generateSlug(event.target.value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizePagePayload(formData);
    const errorMessage = validatePageForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Informations de la page
        </h3>

        <div className="mt-4 grid gap-4">
          <div>
            <label className={labelClass}>
              Titre de la page <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              placeholder="Ex : Historique du Judo, Projet du club..."
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>
              Slug URL <span className="text-rose-500">*</span>
            </label>

            <div className="relative">
              <FaLink className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleSlugChange}
                placeholder="historique-du-judo"
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-cyan-500/10"
              />
            </div>

            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              URL publique prévue :{" "}
              <span className="font-bold text-slate-700 dark:text-slate-200">
                /page/{formData.slug || "slug-de-la-page"}
              </span>
            </p>
          </div>

          <div>
            <label className={labelClass}>Introduction</label>
            <textarea
              name="introduction"
              value={formData.introduction}
              onChange={handleChange}
              rows={5}
              placeholder="Courte introduction affichée en haut de la page."
              className={inputClass}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Ordre dans le menu</label>
              <input
                type="number"
                name="ordre_menu"
                value={formData.ordre_menu}
                onChange={handleChange}
                min="0"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Meta title</label>
              <input
                type="text"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleChange}
                placeholder="Titre SEO de la page"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Meta description</label>
            <textarea
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
              rows={3}
              placeholder="Description courte pour le référencement."
              className={inputClass}
            />
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
              <div>
                <p className="font-bold text-slate-900 dark:text-white">
                  Page active
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Une page active peut être affichée côté visiteur.
                </p>
              </div>

              <input
                type="checkbox"
                name="actif"
                checked={Boolean(formData.actif)}
                onChange={handleChange}
                className="h-5 w-5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
            </label>

            <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
              <div>
                <p className="font-bold text-slate-900 dark:text-white">
                  Afficher dans le menu
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  La page apparaîtra dans la navigation publique.
                </p>
              </div>

              <input
                type="checkbox"
                name="afficher_menu"
                checked={Boolean(formData.afficher_menu)}
                onChange={handleChange}
                className="h-5 w-5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
            </label>
          </div>
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : mode === "create"
                ? "Créer la page"
                : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}